import { Box, Flex } from "@chakra-ui/react";
import { DateInput } from "components/DateInput";
import { FormattedInput } from "components/FormInputs/FormattedInput";
import { Location } from "components/FormInputs/Location";
import { Heading } from "components/Heading";
import { FieldForm as UnTypedFieldForm } from "components/RFQ/Input/Input";
import UserSelectDropDown from "components/RFQ/UserSelectDropDown/UserSelectDropDown";
import TimePickerField from "components/TimePicker/TimePicker";
import Tooltip from "components/Tooltip";
import { FormikContextType, useFormikContext } from "formik";
import React, { useMemo } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { Rfx, RfxType } from "newui/features/Rfx/types/rfx.types";

const ProjectDetailContainer = styled.div(() => [tw`flex flex-col w-full`]);

interface ProjectDetailProps {
	onRfxTypeChange: (e: any) => void;
	titlePage: string;
	subTitlePage?: string;
	rfxTypes: RfxType[];
	rfxData?: Rfx;
}

// TODO: Need to remove this once FieldForm is a TypeScript
const FieldForm = UnTypedFieldForm as React.FC<any>;

export const ProjectDetail: React.FC<ProjectDetailProps> = ({
	onRfxTypeChange,
	titlePage,
	subTitlePage,
	rfxTypes,
	rfxData,
}) => {
	// Infer correct type for Formik context
	const { setFieldError } = useFormikContext<
		Record<string, any>
	>() as FormikContextType<Record<string, any>>;

	const isOnboardingProject = rfxData?.project?.is_onboarding;

	const typeOptions = useMemo(() => {
		return rfxTypes
			?.filter((item) =>
				// filter out the onboarding type if not onboarding project
				isOnboardingProject ? true : item.code.toLowerCase() !== "onb"
			)
			.sort((a, b) => a.sort_order - b.sort_order)
			.map((type) => ({
				label: type.name,
				value: type.code,
			}));
	}, [rfxTypes, isOnboardingProject]);

	return (
		<ProjectDetailContainer>
			<div
				id="subheader-page"
				className="py-8"
			>
				<div
					id="subheader-page-title"
					className="text-xl font-semibold text-primary-900"
				>
					<Heading
						fontSize="36px"
						color="#003CE9"
					>
						{titlePage}
					</Heading>
				</div>
				{subTitlePage && (
					<div
						id="subheader-page-subtitle"
						className="mt-1 font-roboto text-sm text-tertiary-600"
					>
						{subTitlePage}
					</div>
				)}
			</div>

			<div
				id="project-detail-content"
				className="relative h-full min-h-[60vh]"
			>
				<Flex
					gap={8}
					className="items-baseline"
				>
					<FieldForm
						label="Request Title"
						placeholder="Eg. Request for Lead Design and Engineering Services"
						name="name"
						required
						className="mb-6"
						maxLength={255}
					/>
					<FieldForm
						label="RFx ID Number"
						placeholder="RFx123"
						name="rfq_id_number"
						className="mb-6"
						maxLength={50}
					/>
				</Flex>

				<Flex
					gap={8}
					className="items-start"
				>
					<Tooltip
						shouldWrapChildren={false}
						placement="top"
						label={
							isOnboardingProject
								? "This was created as an Onboard & Pre-qualify request and cannot be changed."
								: ""
						}
					>
						<Box
							display="block"
							w="full"
						>
							<FieldForm
								label="Type of Request"
								placeholder="Select type"
								name="type"
								type="select"
								checkIcon
								options={typeOptions}
								required
								className="mb-6"
								testId="rfx_type_select"
								disabled={isOnboardingProject}
								onChange={onRfxTypeChange}
							/>
						</Box>
					</Tooltip>
					{
						// @ts-ignore
						<Location
							label="Project Location"
							name="address"
							errorBottom="-3px"
							rfq
							rfqIcon="search-sm"
							className="relative"
							required
							allowInternational
							inputTestId="project_location"
							dropdownTestId="location_auto_complete"
						/>
					}
				</Flex>

				<Flex
					gap={8}
					className="items-baseline pb-4"
				>
					{
						// @ts-ignore
						<UserSelectDropDown
							label="RFx Lead"
							name="rfx_lead_id"
							rfx={rfxData}
							description={`The RFx Lead manages this RFx, including receiving notifications, updates, and responding to Bidder queries.
The RFx Lead email address and phone number will also be displayed on the RFx documents shared to all participants.`}
						/>
					}
				</Flex>

				<Flex>
					{
						// @ts-ignore
						<FormattedInput
							label="Project / Asset Description"
							name="description"
							characterLimit={3000}
							rfq
							className="w-full"
							placeholder="Enter a description..."
							subTitle="Briefly describe the project or asset. Consider the scale, size, quantity or staging and any other important features."
							setFieldError={setFieldError}
						/>
					}
				</Flex>

				<Flex gap={8}>
					<FieldForm
						label="Client"
						placeholder="Enter full name"
						name="client"
						required
						className="mb-6"
					/>
				</Flex>

				<Flex className="items-baseline gap-8">
					{
						// @ts-ignore
						<DateInput
							icon
							label="Submission Deadline"
							name="deadline_date"
							minDate={new Date()}
							className="!font-regular flex-3 w-[80%] !border-fg-disabled_subtle"
							required
						/>
					}
					<div className="sm:w-[25%]">
						{
							// @ts-ignore
							<TimePickerField
								className="mt-9 w-full flex-1"
								name="deadline_time"
								nameTimezone="submission_deadline_timezone"
								label=""
								hasDefaultValue
							/>
						}
					</div>
				</Flex>

				<Flex>
					{
						// @ts-ignore
						<FormattedInput
							label="Requested Services"
							name="escope_of_service"
							characterLimit={5000}
							rfq
							className="w-full"
							placeholder="Enter a description..."
							subTitle="Provide a brief description of the scope of services needed for this project."
							containerClassName="!mt-4"
						/>
					}
				</Flex>
			</div>
		</ProjectDetailContainer>
	);
};
